import {diffArrays, diffWordsWithSpace} from "diff";
import {snapshotValuesEqual} from "@/services/wingman/mutation-proposal";
import type {SnapshotChange} from "./useCaseSnapshot";

export type StepChange = {kind: SnapshotChange | "unchanged"; moved?: {from: number; to: number}};
const asRecord = (value: unknown): Record<string, unknown> | undefined =>
   value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
const stepId = (value: unknown) => typeof asRecord(value)?.id === "string" ? asRecord(value)!.id : undefined;

/** Match identities first, then exact content, then similar id-less steps. Never reuse a match. */
export function diffUseCaseSteps(before: unknown[], after: unknown[]): {before: StepChange[]; after: StepChange[]; pairs: ReadonlyMap<number, number>} {
   const pairs = new Map<number, number>();
   const used = new Set<number>();
   const match = (test: (a: unknown, b: unknown) => boolean) => before.forEach((item, from) => {
      if (pairs.has(from)) return;
      const to = after.findIndex((other, index) => !used.has(index) && test(item, other));
      if (to >= 0) {pairs.set(from, to); used.add(to);}
   });
   match((a, b) => stepId(a) !== undefined && stepId(a) === stepId(b));
   match(snapshotValuesEqual);
   match((a, b) => {
      if (stepId(a) !== undefined || stepId(b) !== undefined) return false;
      const left = asRecord(a)?.description, right = asRecord(b)?.description;
      if (typeof left !== "string" || typeof right !== "string" || !left.trim() || !right.trim()) return false;
      const shared = diffWordsWithSpace(left, right).filter(part => !part.added && !part.removed)
         .reduce((length, part) => length + part.value.trim().length, 0);
      return shared / Math.max(left.trim().length, right.trim().length) >= 0.5;
   });
   const originalOrder = [...pairs.keys()].sort((a, b) => a - b);
   const newOrder = [...pairs].sort((a, b) => a[1] - b[1]).map(([from]) => from);
   const stayed = new Set(diffArrays(originalOrder, newOrder).filter(run => !run.added && !run.removed).flatMap(run => run.value));
   const changes = {
      before: before.map((): StepChange => ({kind: "removed"})),
      after: after.map((): StepChange => ({kind: "added"})),
   };
   pairs.forEach((to, from) => {
      const change: StepChange = {kind: snapshotValuesEqual(before[from], after[to]) ? "unchanged" : "edited",
         ...(stayed.has(from) ? {} : {moved: {from: from + 1, to: to + 1}})};
      changes.before[from] = change;
      changes.after[to] = change;
   });
   return {...changes, pairs};
}
